import { inventoryLotsByLotIdApi } from 'api/inventory';
import { useEffect, useMemo, useRef, useState } from 'react';
// The SAME rules Fill Product's "record a lot manually" panel applies, imported rather than
// restated, so a hand-recorded lot behaves identically whichever screen recorded it.
import {
  LOT_NUMBER_MAX_LENGTH,
  UNSAFE_LOT_CHARACTERS,
  getDispensableBudHelperText,
  getLotEntriesForNdc,
  getLotNdcMismatch,
  isLotConfirmed,
  isValidLotNumber,
} from 'modules/StockManagement/utils/stockLineValidation';

import { lotRowStatus } from '../utils/matchScannedLotToInventory';
import ndcsMatch from '../utils/ndcsMatch';

export const normalizeLot = (v) =>
  String(v ?? '')
    .trim()
    .toUpperCase();

// Same pause as Fill Product: a lot number is read off the vial and typed straight through, so
// the lookup coalesces into one request after the last character.
const LOT_LOOKUP_DEBOUNCE_MS = 1500;

export const budOf = (lot) =>
  lot?.expirationDate ? String(lot.expirationDate).substring(0, 10) : '';

const EMPTY_LOOKUP = { lotNumber: '', entries: [], loading: false };

/** State and validation behind AddDrugByLotDialog's hand-recorded (unverified) lot path — the
 *  Rx scan station's counterpart to Fill Product's "record a lot manually" panel (POM-6083).
 *
 *  The NDC comes first and scopes everything: the product's closed NDC list, its depleted lots
 *  and its dispensable stock AT THIS PHARMACY (`facility`, fetched once per session by
 *  useFacilityLots) default to the NDC read off the rejected vial, else the Rx's own. The dialog
 *  then matches the typed lot against that stock under that NDC; `active` is true while the
 *  typed lot has NO such match and so is being recorded by hand.
 *  While active:
 *   - the typed lot number is looked up across inventory (inventoryLotsByLotId, debounced) so a
 *     lot owned by a different NDC is refused, and one that already exists at this pharmacy is
 *     claimed by id rather than duplicated;
 *   - the BUD of a resolved existing lot is adopted and locked (it is part of that row's
 *     identity); otherwise the BUD is REQUIRED and must be tomorrow or later — the operator
 *     holding the vial can read it off the label, and a lot at or past its BUD cannot be
 *     dispensed. Same rule as Fill Product (getDispensableBudHelperText). */
const useManualLotEntry = ({
  open,
  available,
  productId,
  fulfillingPharmacyId,
  // See useFacilityLots.
  facility,
  rxNdc,
  // The NDC read off a vial the scanner rejected, when the dialog was opened from that
  // rejection. Outranks the Rx's own NDC as the default: it is what is printed on the vial.
  seedNdc,
  typedLot,
  hasListingMatch,
  // The product's dispensable stock at this pharmacy, all NDCs (see AddDrugByLotDialog's
  // stockLots source). Used to tell the operator when a typed lot is in stock under ANOTHER of
  // the product's NDCs — the fix is to switch the NDC, not to record the lot by hand.
  productLots,
}) => {
  const [manualNdc, setManualNdc] = useState('');
  const [manualBud, setManualBud] = useState('');
  const [confirmLotNumber, setConfirmLotNumber] = useState('');
  const [lookup, setLookup] = useState(EMPTY_LOOKUP);
  const lookupSeq = useRef(0);

  const active = available && !!typedLot && !hasListingMatch;

  const ndcOptions = useMemo(() => facility?.ndcOptions ?? [], [facility]);
  const recordableLoading = !!facility?.loading;
  const recordableNotice = facility?.notice ?? null;

  // Default the NDC on every open (and whenever the option list arrives): the NDC read off the
  // vial, else the Rx's own NDC when inventory holds it, else the product default.
  useEffect(() => {
    if (!open || !available) return;
    const preferred =
      ndcOptions.find((option) => seedNdc && ndcsMatch(option.ndc, seedNdc)) ??
      ndcOptions.find((option) => rxNdc && ndcsMatch(option.ndc, rxNdc)) ??
      ndcOptions.find((option) => option.isDefault) ??
      ndcOptions[0];
    setManualNdc(preferred?.ndc ?? '');
  }, [open, available, ndcOptions, rxNdc, seedNdc]);

  useEffect(() => {
    if (!active) {
      setLookup(EMPTY_LOOKUP);
      return undefined;
    }
    setLookup((prev) =>
      prev.lotNumber === typedLot ? prev : { lotNumber: typedLot, entries: [], loading: true }
    );
    const seq = lookupSeq.current + 1;
    lookupSeq.current = seq;
    const timer = setTimeout(() => {
      inventoryLotsByLotIdApi({ lotId: typedLot })
        .then((entries) => {
          if (lookupSeq.current !== seq) return;
          setLookup({ lotNumber: typedLot, entries: entries ?? [], loading: false });
        })
        .catch(() => {
          if (lookupSeq.current !== seq) return;
          // "Nothing found" rather than a block — core still refuses a foreign lot itself.
          setLookup({ lotNumber: typedLot, entries: [], loading: false });
        });
    }, LOT_LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, typedLot]);

  const hasNdcChoices = ndcOptions.length > 0;

  const lookupIsCurrent = lookup.lotNumber === typedLot;
  const lookupPending = active && (!lookupIsCurrent || lookup.loading);
  const foreignNdcLots = lookupIsCurrent
    ? getLotNdcMismatch({ entries: lookup.entries, ndc: manualNdc })
    : [];

  // In stock under one of the product's OTHER NDCs (per the scan-app listing). Not a foreign
  // lot — same drug — but not something to record by hand either: the operator has the wrong
  // NDC selected.
  const stockedUnderOtherNdc = active
    ? ((productLots ?? []).find(
        (row) =>
          normalizeLot(row.lotNumber) === normalizeLot(typedLot) &&
          row.ndc &&
          manualNdc &&
          !ndcsMatch(row.ndc, manualNdc)
      ) ?? null)
    : null;

  // Zero-quantity lots for the NDC in the field, scoped by NDC because a lot number is only
  // unique within one.
  const depletedLotChoices = useMemo(
    () =>
      (facility?.depletedLots ?? []).filter((lot) => manualNdc && ndcsMatch(lot.ndc, manualNdc)),
    [facility, manualNdc]
  );

  // Same-NDC rows for the typed lot AT THIS PHARMACY that the lookup found. The facility lists
  // can lag a receipt by a few minutes, so this is the freshest word on whether the lot is here.
  const lookupRowsHere = useMemo(
    () =>
      lookupIsCurrent && active
        ? getLotEntriesForNdc({ entries: lookup.entries, ndc: manualNdc }).filter(
            (entry) =>
              entry.fulfillingPharmacyId === fulfillingPharmacyId &&
              normalizeLot(entry.lotNumber) === normalizeLot(typedLot)
          )
        : [],
    [lookupIsCurrent, active, lookup.entries, manualNdc, fulfillingPharmacyId, typedLot]
  );

  // Stock on THIS shelf that the picker did not offer — dispensable (the facility list is
  // stale) or expired. Neither may be recorded by hand: that would claim stock as unverified.
  const stockedHereRow = lookupRowsHere.find((row) => lotRowStatus(row) !== 'depleted') ?? null;

  // Existing DEPLETED rows the typed lot resolves to: the product's depleted lots, plus any
  // drained same-NDC row here the lookup found. Several rows under different BUDs are
  // legitimate (a lot number repeats across shipments) and cannot be guessed — the typed BUD
  // picks one, or the operator must supply it.
  const existingRows = useMemo(() => {
    if (!active) return [];
    const fromDepleted = depletedLotChoices.filter(
      (lot) => normalizeLot(lot.lotNumber) === normalizeLot(typedLot)
    );
    const fromLookup = lookupRowsHere.filter((row) => lotRowStatus(row) === 'depleted');
    const byId = new Map();
    [...fromDepleted, ...fromLookup].forEach((lot) => {
      if (!byId.has(lot.inventoryLotId)) byId.set(lot.inventoryLotId, lot);
    });
    return [...byId.values()];
  }, [active, depletedLotChoices, typedLot, lookupRowsHere]);

  let existingLot = null;
  if (existingRows.length === 1) [existingLot] = existingRows;
  else if (existingRows.length > 1 && manualBud) {
    existingLot = existingRows.find((lot) => budOf(lot) === manualBud) ?? null;
  }
  const budAmbiguous = existingRows.length > 1 && !existingLot;

  const adoptedBud = existingLot ? budOf(existingLot) : '';
  useEffect(() => {
    if (adoptedBud) setManualBud(adoptedBud);
  }, [adoptedBud]);

  const budLocked = !!existingLot && !!adoptedBud;
  const needsLotConfirmation = active && !existingLot;
  const budMissing = active && !budLocked && !manualBud.trim();
  const budTooEarly = budLocked ? '' : getDispensableBudHelperText(manualBud);

  const error = (() => {
    if (!active) return null;
    if (recordableLoading) return 'Loading the NDCs for this product…';
    if (!hasNdcChoices) {
      return 'No NDC is on record for this product, so a lot cannot be recorded by hand. Ask an inventory user to add the product first.';
    }
    if (!isValidLotNumber(typedLot)) {
      return `Check the lot number — no spaces or ${UNSAFE_LOT_CHARACTERS}, up to ${LOT_NUMBER_MAX_LENGTH} characters.`;
    }
    if (!manualNdc) return 'Select the NDC printed on the vial.';
    if (stockedUnderOtherNdc) {
      return `Lot ${stockedUnderOtherNdc.lotNumber} is in stock under NDC ${stockedUnderOtherNdc.ndc} — select that NDC to add it as a scanned vial.`;
    }
    if (stockedHereRow) {
      return lotRowStatus(stockedHereRow) === 'expired'
        ? `Lot ${typedLot} is on this pharmacy's shelf but past its BUD — it cannot be dispensed, and must not be recorded by hand.`
        : `Lot ${typedLot} is in stock at this pharmacy — close and reopen this dialog to pick it, or re-scan the vial.`;
    }
    if (foreignNdcLots.length > 0) {
      const owners = foreignNdcLots
        .map((entry) =>
          entry.drugName ? `${entry.drugName} (NDC ${entry.ndc})` : `NDC ${entry.ndc}`
        )
        .join(', ');
      return `Lot ${typedLot} already belongs to ${owners}. A lot number belongs to one product, so either the drug or the lot number is wrong — check the vial.`;
    }
    if (budAmbiguous) {
      return `Lot ${typedLot} exists under more than one BUD — enter the BUD printed on the vial to say which.`;
    }
    if (needsLotConfirmation && !isLotConfirmed(typedLot, confirmLotNumber)) {
      return confirmLotNumber.trim()
        ? 'The two lot numbers do not match. Re-enter it exactly as typed above.'
        : 'Re-enter the lot number to confirm it.';
    }
    if (budMissing) return 'Enter the expiration date (BUD) printed on the vial.';
    // The input carries a min, so this is only reachable by typing a date directly into it.
    if (budTooEarly) {
      return 'The expiration date must be tomorrow or later — an expired lot cannot be dispensed.';
    }
    return null;
  })();

  // A new lot number is a new claim: the confirmation and any adopted BUD described the old one.
  const onLotNumberChanged = () => {
    setConfirmLotNumber('');
    if (budLocked) setManualBud('');
  };

  // Changing the NDC re-scopes everything under it: the depleted-lot list, a resolved row, and
  // the BUD adopted from it.
  const onNdcChange = (ndc) => {
    if (ndc === manualNdc) return;
    setManualNdc(ndc);
    setConfirmLotNumber('');
    setManualBud('');
  };

  const reset = () => {
    setManualBud('');
    setConfirmLotNumber('');
    setLookup(EMPTY_LOOKUP);
  };

  /** The vial fields to add — see ScanSessionContext.addVialManually / buildLotAllocations. */
  const buildVialFields = ({ productStrength }) => {
    const ndcOption = ndcOptions.find((option) => option.ndc === manualNdc);
    return {
      drugName: ndcOption?.drugName || existingLot?.drugName || '',
      manufacturer: ndcOption?.manufacturer || '',
      strength: existingLot?.strength || productStrength || '',
      ndc: manualNdc,
      lotNumber: existingLot?.lotNumber ?? typedLot,
      productId,
      inventoryLotId: existingLot?.inventoryLotId ?? null,
      expirationDate: manualBud,
      // A BUD typed for a resolved lot that had none has to reach the API alongside the real lot
      // id, where it is otherwise ignored — see buildLotAllocations.
      suppliesBud: !!existingLot && !adoptedBud,
      isNewLot: !existingLot,
    };
  };

  return {
    active,
    manualNdc,
    manualBud,
    setManualBud,
    confirmLotNumber,
    setConfirmLotNumber,
    ndcOptions,
    hasNdcChoices,
    recordableLoading,
    recordableNotice,
    depletedLotChoices,
    existingLot,
    budLocked,
    budMissing,
    budTooEarly,
    needsLotConfirmation,
    lookupPending,
    error,
    onLotNumberChanged,
    onNdcChange,
    reset,
    buildVialFields,
  };
};

export default useManualLotEntry;
